import { create } from "zustand";
import {
  buildWindows,
  loadDeviceHistory,
  processInBatches,
  DEVICE_CONCURRENCY,
} from "../../pages/reports/utils/trackingHistoryWindows";
import {
  mapApiPoint,
  finalizeRoutePoints,
  type ApiTrackingPoint,
  type RoutePoint,
} from "../admin/vehicleRouteTrackingStore";

export const REPORT_ROUTE_COLORS = [
  "#2563EB",
  "#DB2777",
  "#16A34A",
  "#D97706",
  "#7C3AED",
  "#0891B2",
  "#DC2626",
  "#65A30D",
];

export interface ReportRoute {
  deviceId: string;
  vehicleName: string;
  color: string;
  points: RoutePoint[];
  total: number;
  loading: boolean;
  error: string | null;
}

export interface ReportTrackingDevice {
  deviceId: string;
  vehicleName: string;
  brandName?: string | null;
  brandModel?: string | null;
}

interface ReportTrackingState {
  routes: Record<string, ReportRoute>;
  order: string[];
  loading: boolean;
  addresses: Record<string, string>;
  from: string | null;
  to: string | null;
  tz: string | null;
}

interface ReportTrackingActions {
  loadRange: (
    devices: ReportTrackingDevice[],
    from: string,
    to: string,
    opts?: { tz?: string; address?: boolean },
  ) => Promise<void>;
  clear: () => void;
}

type ReportTrackingStore = ReportTrackingState & ReportTrackingActions;

const emptyState: ReportTrackingState = {
  routes: {},
  order: [],
  loading: false,
  addresses: {},
  from: null,
  to: null,
  tz: null,
};

export const useReportTrackingStore = create<ReportTrackingStore>()((set) => ({
  ...emptyState,

  loadRange: async (devices, from, to, opts) => {
    const resolvedTz = opts?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const withAddress = opts?.address ?? false;
    const order = devices.map((d) => d.deviceId);

    const windows = buildWindows(new Date(from).getTime(), new Date(to).getTime());

    const initial: Record<string, ReportRoute> = {};
    devices.forEach((d, i) => {
      initial[d.deviceId] = {
        deviceId: d.deviceId,
        vehicleName: d.vehicleName,
        color: REPORT_ROUTE_COLORS[i % REPORT_ROUTE_COLORS.length],
        points: [],
        total: 0,
        loading: true,
        error: null,
      };
    });

    set({ routes: initial, order, loading: true, addresses: {}, from, to, tz: resolvedTz });

    const allAddresses: Record<string, string> = {};

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { points: rawPoints, addresses, failedChunks, errorDetails } = await loadDeviceHistory(
        d.deviceId,
        windows,
        resolvedTz,
        withAddress,
      );

      Object.assign(allAddresses, addresses);

      const points = rawPoints
        .map((raw) => mapApiPoint(raw as ApiTrackingPoint, d.brandName, d.brandModel))
        .filter((p) => p.lat != null && p.lng != null);

      finalizeRoutePoints(points);

      set((state) => {
        const current = state.routes[d.deviceId];
        if (!current) return state;
        return {
          routes: {
            ...state.routes,
            [d.deviceId]: {
              ...current,
              points,
              total: points.length,
              loading: false,
              error:
                failedChunks > 0
                  ? `${failedChunks} tramo(s) con error — ${errorDetails[0] ?? "sin detalle"}`
                  : null,
            },
          },
        };
      });
    });

    set({ loading: false, addresses: allAddresses });
  },

  clear: () => set({ ...emptyState }),
}));
